// 記事詳細でスマホ幅の場合にテーブルをスクロールできるようにする
// https://github.com/NinNinInc/tabi/pull/2335
// https://github.com/NinNinInc/tabi/issues/2321
// に対応するためにLexicalのコア機能のTableNodeをオーバーライド
import { $isTableRowNode, TableNode, TableRowNode } from "@lexical/table";
import {
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  SerializedElementNode,
  $applyNodeReplacement,
} from "lexical";

import { addClassNamesToElement, isHTMLElement } from "@lexical/utils";

export type SerializedCustomTableNode = SerializedElementNode;

export class CustomTableNode extends TableNode {
  __isScrollable: boolean;

  static getType(): string {
    return "custom-table";
  }

  static clone(node: CustomTableNode): CustomTableNode {
    return new CustomTableNode(node.__key);
  }

  static importJSON(_serializedNode: SerializedCustomTableNode): TableNode {
    return $createCustomTableNode();
  }

  exportJSON(): SerializedElementNode {
    return {
      ...super.exportJSON(),
      type: "custom-table",
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const tableElement = document.createElement("table");

    const classes = [config.theme.table];
    if (this.__isScrollable) {
      classes.push(config.theme.tableScrollable);
    }
    addClassNamesToElement(tableElement, ...classes);

    return tableElement;
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    return {
      ...super.exportDOM(editor),
      after: (tableElement) => {
        if (tableElement) {
          const newElement = tableElement.cloneNode() as ParentNode;
          const colGroup = document.createElement("colgroup");
          const tBody = document.createElement("tbody");
          if (isHTMLElement(tableElement)) {
            tBody.append(...tableElement.children);
          }
          const firstRow = this.getFirstChildOrThrow<TableRowNode>();

          if (!$isTableRowNode(firstRow)) {
            throw new Error("Expected to find row node.");
          }

          const colCount = firstRow.getChildrenSize();

          for (let i = 0; i < colCount; i++) {
            const col = document.createElement("col");
            colGroup.append(col);
          }

          newElement.replaceChildren(colGroup, tBody);

          const wrapElement = document.createElement("div");
          const innerElement = document.createElement("div");
          const theme = editor._config.theme;
          const blockClassName = theme.tableBlock;
          const blockScrollableClassName = theme.tableBlockScrollable;
          const innerClassName = theme.tableBlockInner;
          const scrollableClassName = theme.tableScrollable;
          if (blockClassName !== undefined) {
            wrapElement.className = blockClassName;
          }
          if (innerClassName !== undefined) {
            innerElement.className = innerClassName;
          }
          if (isHTMLElement(newElement)) {
            if (colCount >= 4) {
              wrapElement.classList.add(blockScrollableClassName);
              newElement.classList.add(scrollableClassName);
              newElement.setAttribute("data-col-count", String(colCount));
            }
          }
          innerElement.append(newElement);
          wrapElement.append(innerElement);

          return wrapElement as HTMLElement;
        }
      },
    };
  }

  setIsScrollable(isScrollable: boolean): void {
    const self = this.getWritable();
    self.__isScrollable = isScrollable;
  }
}

export function $createCustomTableNode(): CustomTableNode {
  return $applyNodeReplacement(new CustomTableNode());
}
